// Show-level dialogs: people and invitations, history and checkpoints, changes to review, and
// the show's portability and lifecycle. Every action is authorized by the server; these screens
// only offer what the person's role allows.

import { useEffect, useState } from "preact/hooks";
import { recordHash } from "../../format/ids.ts";
import { findRecord, type Op, recordKey } from "../../format/ops.ts";
import type { AnyRecord, RecordType } from "../../format/types.ts";
import { api, post } from "../account.ts";
import { queue } from "../edits.ts";
import { deleteProposal, type Proposal } from "../idb.ts";
import { eraseLocal } from "../local.ts";
import { ix, notify, pending, proposals, serverDoc, show } from "../state.ts";
import { downloadPending, resync } from "../sync.ts";
import { confirmAction } from "./Confirmation.tsx";
import { ActionRow, Group, InfoRow, Sheet } from "./kit.tsx";

// ---------------------------------------------------------------- people

interface Member {
  userId: string;
  name: string;
  email: string;
  role: "owner" | "editor" | "viewer";
}
interface Invitation {
  id: string;
  email: string;
  role: "editor" | "viewer";
  expiresAt: number;
  state: "pending" | "expired" | "revoked" | "accepted";
}

export function Members() {
  const owner = show.value.role === "owner";
  const [data, setData] = useState<{ members: Member[]; invitations: Invitation[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"editor" | "viewer">("editor");
  const base = `/api/shows/${show.value.showId}`;
  const load = () => void api<{ members: Member[]; invitations: Invitation[] }>(`${base}/members`).then(setData, (e: Error) => setError(e.message));
  useEffect(load, []);
  const act = async (path: string, body: unknown = {}, confirmation?: Parameters<typeof confirmAction>[0]): Promise<boolean> => {
    if (confirmation && !(await confirmAction(confirmation))) return false;
    setError(null);
    try {
      await post(`${base}/${path}`, body);
      load();
      void resync();
      return true;
    } catch (e) {
      setError((e as Error).message);
      return false;
    }
  };
  const me = show.value.accountId;
  return (
    <Sheet title="People" back="menu" wide>
      {error && <p class="error" role="alert">{error}</p>}
      <Group title="Members" footer="Everyone can see every department and note layer, export and prepare offline copies. Editors change the book; viewers can't. Only the owner manages people and the show's lifecycle.">
        {data?.members.map((m) => (
          <div class="row member" data-member={m.email}>
            <span class="row-label">
              {m.name}
              {m.userId === me ? " (you)" : ""} <span class="hint">{m.email}</span>
            </span>
            {owner && m.role !== "owner" ? (
              <span class="row-buttons">
                <select aria-label={`Role for ${m.email}`} value={m.role} onChange={(e) => void act(`members/${m.userId}/role`, { role: (e.target as HTMLSelectElement).value })}>
                  <option value="editor">Editor</option>
                  <option value="viewer">Viewer</option>
                </select>
                <button type="button" onClick={() => void act(`members/${m.userId}/owner`, {}, { title: "Transfer show ownership", message: `Make ${m.name} the owner? You become an editor and can no longer manage people or delete the show.`, confirmLabel: "Make owner", destructive: true })}>
                  Make owner
                </button>
                <button type="button" class="danger" onClick={() => void act(`members/${m.userId}/remove`, {}, { title: "Remove member", message: `Remove ${m.name} from this show? Copies already on their devices can't be erased remotely.`, confirmLabel: "Remove member", destructive: true })}>
                  Remove
                </button>
              </span>
            ) : (
              <span class="row-detail">{m.role}</span>
            )}
          </div>
        ))}
      </Group>
      {owner && (
        <>
          <Group title="Invite" footer="Invitations go to one email address, expire in 7 days and can only be accepted by someone signed in with that address.">
            <form
              class="row invite"
              onSubmit={(e) => {
                e.preventDefault();
                void act("invitations", { email, role }).then(() => setEmail(""));
              }}
            >
              <input type="email" required placeholder="name@example.org" aria-label="Email to invite" value={email} onInput={(e) => setEmail((e.target as HTMLInputElement).value)} />
              <select aria-label="Invite as" value={role} onChange={(e) => setRole((e.target as HTMLSelectElement).value as "editor" | "viewer")}>
                <option value="editor">Editor</option>
                <option value="viewer">Viewer</option>
              </select>
              <button type="submit" class="primary">
                Send invitation
              </button>
            </form>
          </Group>
          {!!data?.invitations.length && (
            <Group title="Invitations">
              {data.invitations.map((i) => (
                <div class="row" data-invitation={i.email}>
                  <span class="row-label">
                    {i.email} <span class="hint">{i.role} · {i.state}{i.state === "pending" ? ` until ${new Date(i.expiresAt).toLocaleDateString()}` : ""}</span>
                  </span>
                  <span class="row-buttons">
                    {i.state === "pending" && <button type="button" onClick={() => void act(`invitations/${i.id}/revoke`)}>Revoke</button>}
                    {i.state !== "accepted" && <button type="button" onClick={() => void act(`invitations/${i.id}/reissue`)}>Send again</button>}
                  </span>
                </div>
              ))}
            </Group>
          )}
        </>
      )}
      {!owner && (
        <Group>
          <ActionRow
            label="Leave this show"
            onClick={() =>
              void act(`members/${me}/remove`, {}, { title: "Leave show", message: "Leave this show? You'll lose access; unsent changes stay on this device for you to download.", confirmLabel: "Leave show", destructive: true }).then((success) => {
                if (success) location.href = "/";
              })
            }
          />
        </Group>
      )}
    </Sheet>
  );
}

// ---------------------------------------------------------------- history

interface ChangeRow {
  revision: number;
  generation: number;
  kind: "create" | "edit" | "restore";
  actor_name: string;
  label: string;
  record_count: number;
  created_at: number;
}
interface CheckpointRow {
  id: string;
  revision: number;
  name: string;
  created_by_name: string;
  created_at: number;
}

export function History() {
  const [data, setData] = useState<{ changes: ChangeRow[]; checkpoints: CheckpointRow[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const base = `/api/shows/${show.value.showId}`;
  const load = () => void api<{ changes: ChangeRow[]; checkpoints: CheckpointRow[] }>(`${base}/history`).then(setData, (e: Error) => setError(e.message));
  useEffect(load, []);
  const owner = show.value.role === "owner" && show.value.state === "active";
  const restore = async (body: { revision?: number; checkpoint?: string }, what: string) => {
    const waiting = pending.value.filter((e) => !e.held);
    const waitingIds = new Set(waiting.map((e) => e.id));
    if (!(await confirmAction({ title: "Restore book", message: `Restore the book to ${what}? This makes a new version that matches it; later history stays. ${waiting.length ? `Your ${waiting.length} unsent change${waiting.length === 1 ? "" : "s"} will be held for review. ` : ""}Other people's unsent changes will also be held for their review.`, confirmLabel: "Restore book", destructive: true }))) return;
    const currentWaiting = pending.value.filter((e) => !e.held);
    if (currentWaiting.length !== waitingIds.size || currentWaiting.some((e) => !waitingIds.has(e.id))) {
      setError("Your unsent changes changed while you were confirming. Review them and try restoring again.");
      return;
    }
    setBusy(true);
    try {
      const r = await post<{ revision: number }>(`${base}/restore`, body);
      notify(`Restored ${what} as version ${r.revision}.`);
      await resync();
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet title="History" back="menu" wide busy={busy}>
      {error && <p class="error" role="alert">{error}</p>}
      <Group title="Checkpoints" footer={owner ? "Restoring makes a new version equal to the checkpoint; nothing later is deleted." : "Only the owner can restore a checkpoint."}>
        {data?.checkpoints.length ? (
          data.checkpoints.map((c) => (
            <div class="row" data-checkpoint={c.name}>
              <span class="row-label">
                {c.name} <span class="hint">version {c.revision} · {c.created_by_name} · {new Date(c.created_at).toLocaleString()}</span>
              </span>
              {owner && (
                <button type="button" onClick={() => void restore({ checkpoint: c.id }, `checkpoint "${c.name}"`)}>
                  Restore
                </button>
              )}
            </div>
          ))
        ) : (
          <InfoRow label="None yet" detail="Menu → Make a checkpoint" />
        )}
      </Group>
      <Group title="Saved changes">
        {data?.changes.map((c) => (
          <div class="row" data-revision={c.revision}>
            <span class="row-label">
              {c.label} <span class="hint">version {c.revision} · {c.actor_name} · {new Date(c.created_at).toLocaleString()} · {c.record_count} record{c.record_count === 1 ? "" : "s"}</span>
            </span>
            {owner && c.revision < show.value.revision && (
              <button type="button" onClick={() => void restore({ revision: c.revision }, `version ${c.revision}`)}>
                Restore
              </button>
            )}
          </div>
        ))}
      </Group>
    </Sheet>
  );
}

// ---------------------------------------------------------------- changes to review

/** A short human description of a record, for comparisons. */
function describe(type: RecordType, r: AnyRecord | null | undefined): string {
  if (!r) return "(none: deleted or never existed)";
  const index = ix.value;
  if (type === "cues" && "department" in r) return `${index?.code(r.department) ?? "?"} ${r.number}${r.label ? ` · ${r.label}` : ""}${r.notes ? ` · ${r.notes}` : ""}`;
  if (type === "notes" && "text" in r) return `Note: ${r.text}`;
  if (type === "moments" && "scriptText" in r) return `Mark on p. ${index?.pageLabel(r.anchor.page) ?? r.anchor.page}${r.scriptText ? ` · “${r.scriptText}”` : ""}`;
  if (type === "standbys" && "through" in r) return `Standby on p. ${index?.pageLabel(r.anchor.page) ?? r.anchor.page}`;
  if (type === "departments" && "code" in r) return `Department ${r.code} · ${r.name}`;
  if (type === "noteLayers" && "name" in r) return `Note layer ${r.name}`;
  if (type === "pages" && "rotate" in r) return `PDF page ${r.page} label “${r.label}”`;
  if (type === "project" && "title" in r) return `Title “${r.title}”`;
  return type;
}

const opKey = (op: Op) => (op.op === "put" ? recordKey(op.type, op.record) : op.key);

/** Re-send the proposal based on the current server records: an explicit choice to apply mine over theirs. */
async function reapply(p: Proposal): Promise<void> {
  const d = serverDoc.value;
  if (!d) return;
  const before: (AnyRecord | null)[] = [];
  const ops: Op[] = p.entry.ops.map((op) => {
    const current = findRecord(d, op.type, opKey(op)) ?? null;
    before.push(current);
    return { ...op, base: recordHash(op.type, current ?? undefined) } as Op;
  });
  if (p.seq !== undefined) await deleteProposal(p.seq);
  proposals.value = proposals.value.filter((x) => x !== p);
  await queue(`Reapply: ${p.entry.label}`, ops, before, show.value.generation);
}

async function discard(p: Proposal): Promise<void> {
  if (p.seq !== undefined) await deleteProposal(p.seq);
  proposals.value = proposals.value.filter((x) => x !== p);
}

export function Proposals() {
  const list = proposals.value;
  const d = serverDoc.value;
  return (
    <Sheet title="Changes to review" back="menu" wide>
      <p class="hint">
        These changes weren't saved because the show changed first. Nothing was overwritten. Compare them with what's there now, then reapply yours over it, or discard it. Reapplying something whose mark,
        department or layer was deleted needs that item to exist again first.
      </p>
      {!list.length && <p>Nothing to review.</p>}
      {list.map((p, i) => (
        <section class="group proposal" data-proposal={i}>
          <h3 class="group-title">
            {p.entry.label} · {new Date(p.entry.at).toLocaleString()}
          </h3>
          <div class="group-rows">
            <p class="row">{p.message}</p>
            {p.problems.map((x) => (
              <p class="row error">{x}</p>
            ))}
            <table class="compare">
              <thead>
                <tr>
                  <th>When you edited</th>
                  <th>Now</th>
                  <th>Your change</th>
                </tr>
              </thead>
              <tbody>
                {p.entry.ops.map((op, j) => {
                  const now = d ? findRecord(d, op.type, opKey(op)) : undefined;
                  const changedBy = p.records.find((r) => r.type === op.type && r.key === opKey(op))?.changedBy;
                  return (
                    <tr>
                      <td>{describe(op.type, p.entry.before?.[j])}</td>
                      <td>
                        {describe(op.type, now)}
                        {changedBy && <div class="hint">by {changedBy.name}, {new Date(changedBy.at).toLocaleString()}</div>}
                      </td>
                      <td>{op.op === "delete" ? "Delete" : describe(op.type, op.record)}</td>
                    </tr>
                  );
                })}
                {p.records
                  .filter((r) => !p.entry.ops.some((op) => op.type === r.type && opKey(op) === r.key))
                  .map((r) => (
                    <tr>
                      <td colSpan={3}>Still depends on it: {describe(r.type, r.current)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
            <div class="panel-buttons">
              <button type="button" class="danger" onClick={() => void discard(p)}>
                Discard mine
              </button>
              <span class="spacer" />
              <button type="button" class="primary" disabled={!!show.value.lock} onClick={() => void reapply(p)}>
                Reapply mine
              </button>
            </div>
          </div>
        </section>
      ))}
      {list.length > 0 && (
        <button type="button" onClick={downloadPending}>
          Download all as a file
        </button>
      )}
    </Sheet>
  );
}

// ---------------------------------------------------------------- show settings, portability and lifecycle

export function ShowSettings() {
  const s = show.value;
  const owner = s.role === "owner";
  const base = `/api/shows/${s.showId}`;
  const [error, setError] = useState<string | null>(null);
  const act = async (path: string, confirmation?: Parameters<typeof confirmAction>[0], then?: (r: { id?: string }) => void) => {
    if (confirmation && !(await confirmAction(confirmation))) return;
    try {
      const r = await post<{ id?: string }>(`${base}/${path}`);
      if (then) then(r);
      else await resync();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  return (
    <Sheet title="This show" back="menu">
      {error && <p class="error" role="alert">{error}</p>}
      <Group title="Show">
        <InfoRow label="Title" detail={s.title} />
        <InfoRow label="Your role" detail={s.role} />
        <InfoRow label="State" detail={s.state} />
        <InfoRow label="Saved version" detail={String(s.revision)} />
        <InfoRow label="Source SHA-256" detail={`${s.sourceSha256.slice(0, 16)}…`} />
      </Group>
      <Group title="Portable copy" footer="The bundle holds the untouched source PDF and q2q.json. It imports into any q2cue installation as a new show; it holds no accounts or members.">
        <a class="row nav" href={`${base}/bundle`} download>
          <span class="row-label">Download portable bundle (.zip)</span>
        </a>
        <a class="row nav" href={`${base}/q2q.json`} download>
          <span class="row-label">Download q2q.json only</span>
        </a>
      </Group>
      <Group title="Lifecycle">
        <ActionRow label="Duplicate into a new show I own" onClick={() => void act("duplicate", { title: "Duplicate show", message: "Make a copy of this book as a new show you own? Members and history aren't copied.", confirmLabel: "Duplicate show" }, (r) => r.id && (location.href = `/s/${r.id}/`))} />
        {owner && s.state === "active" && <ActionRow label="Archive (read-only for everyone)" onClick={() => void act("archive")} />}
        {owner && s.state === "archived" && <ActionRow label="Unarchive" onClick={() => void act("unarchive")} />}
        {owner && (
          <ActionRow
            label="Delete show…"
            onClick={() => void act("trash", { title: "Move show to trash", message: "Move this show to the trash? Members lose access now. You can restore it from the shows page for 30 days; after that it's deleted.", confirmLabel: "Move to trash", destructive: true }, () => (location.href = "/"))}
          />
        )}
      </Group>
      <Group title="This device" footer="Removes this show's offline copy and settings from this browser. Unsent changes are erased too; download them first if you need them.">
        <ActionRow
          label="Erase this show's data on this device"
          onClick={async () => {
            const pendingIds = new Set(pending.value.map((e) => e.id));
            const proposalIds = new Set(proposals.value.map((p) => p.entry.id));
            const n = pending.value.length + proposals.value.length;
            if (!(await confirmAction({ title: "Erase local show data", message: n ? `Erase this show's data here, including ${n} change${n === 1 ? "" : "s"} never saved?` : "Erase this show's offline copy and settings from this browser?", confirmLabel: "Erase local data", destructive: true }))) return;
            if (pending.value.length !== pendingIds.size || pending.value.some((e) => !pendingIds.has(e.id)) || proposals.value.length !== proposalIds.size || proposals.value.some((p) => !proposalIds.has(p.entry.id))) {
              setError("Your unsaved changes changed while you were confirming. Review them and try erasing again.");
              return;
            }
            await eraseLocal(s.accountId, s.showId);
            location.href = "/";
          }}
        />
      </Group>
    </Sheet>
  );
}
